import { useEffect, useState } from "react";
import UseCaseCard from "./components/useCaseCard";
import AgentCard from "./components/agentSelectCard";
import Emily from "../assets/emily.png";
//@ts-ignore
import { MouseIcon, Phone, MessageSquare } from "lucide-react";
import axios from "axios";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import RetellCallDashboard from "./components/voice/RetellCallInterface";
import VoicexCallDashboard from "./components/voice/VoicexCallInterface";
import OutboundCallDashboard from "./components/voice/outBoundCall";
import ChatWindow from "./components/chat/chatWindow";
import { newAgentCategories } from "./utils/newPageBuilder";
import { HeartPulse } from "lucide-react";
import {
  getTtsConfigsByAgent,
  TtsConfig
} from "@/components/hooks/useVoiceTtsConfigs";

interface RenderPageProps {
  page_id?: string;
  page_title: string;
  page_path: string;
  page_description?: string;
  page_category: string;
}

interface VoiceOption {
  id: string;
  name: string;
  icon: string;
  description?: string;
  language?: string;
  region?: string;
}

interface FetchedAgent {
  agent_id: string;
  agent_name: string;
  agent_role: string;
  icon: string;
  chat_icon: string;
  feature_list: any[];

  voice: boolean;
  chat: boolean;

  chat_hints: [];
  call_hints?: any[];
  call_hints_table: any[];
  voice_inputs: string[];

  voice_provider: string;
  voice_config: any;
  description?: string;
  call_title?: string;
  sub_description?: string;
  default_voice_id?: string;
  optional_voices?: any[];
  try_speaking?: string[];
  activities?: string[];
  region?: string;
  language?: string;
}

interface Agent {
  id: string;
  callHints: any[];
  callHintsTable: any[];
  name: string;
  description: string;
  subDescription?: string;
  language: string;
  icon: string;
  phoneNumber: string;
  agentName: string;
  chatIcon: string;
  featureList: string[];
  voice: string;
  chat: string;
  chatHints?: any[];
  callTitle?: string;
  voiceInputs: any[];
  voiceProvider: string;
  voiceConfig: any;
  default_voiceId?: string;
  optional_voices: any[];
  try_speaking?: string[];
  activities?: string[];
}

interface SelectedConfig {
  agent: Agent;
  voiceProvider: string;
  selectedVoice: VoiceOption;
}

export default function RenderPageCard({
  page_id,
  page_path,
  page_title,
  page_description,
  page_category
}: RenderPageProps) {
  const [selectedUseCase, setSelectedUseCase] = useState(0);
  const [selectedVoiceIndex, setSelectedVoiceIndex] = useState(0);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isChatDialogOpen, setIsChatDialogOpen] = useState(false);

  const [selectedConfig, setSelectedConfig] = useState<SelectedConfig | null>(
    null
  );

  const fetchAgents = async () => {
    try {
      setLoading(true);

      const category = Object.values(newAgentCategories).find(
        (cat) => cat.path === page_path
      );

      if (category && category.agents) {
        // Type assertion to ensure proper array handling
        const agents = category.agents as Agent[];

        if (!Array.isArray(agents) || agents.length === 0) {
          setAgents([]);
          return;
        }

        const voicexAgents = agents.filter(
          (agent: Agent) => agent.voiceProvider === "voicex"
        );
        const otherAgents = agents.filter(
          (agent: Agent) => agent.voiceProvider !== "voicex"
        );


        const voicexAgentsWithTts = await Promise.all(
          voicexAgents.map(async (agent: Agent) => {
            try {
              const ttsConfigs: TtsConfig[] = await getTtsConfigsByAgent({
                baseUrl: agent.voiceConfig?.baseUrl || "",
                agent_id: agent.id,
                tenant_id: "",
                workspace_id: ""
              });

              // Map TTS configs to optional_voices format
              const mappedTtsVoices = ttsConfigs.map((config: TtsConfig) => ({
                id: config.tts_config_id,
                name: config.display_text,
                icon: config.display_img,
                description: config.name,
                language: config.language,
                region: ""
              }));

              return {
                ...agent,
                optional_voices: [...agent.optional_voices, ...mappedTtsVoices]
              };
            } catch (ttsError) {
              console.error(
                `Failed to fetch TTS configs for agent ${agent.id}:`,
                ttsError
              );
              return agent;
            }
          })
        );

        // Combine processed VoiceX agents with other agents
        const allProcessedAgents = [...otherAgents, ...voicexAgentsWithTts];
        setAgents(allProcessedAgents);
      } else {
        setAgents([]);
      }
    } catch (error) {
      console.error("Failed to fetch agents:", error);
      setError("Failed to fetch agents");
    } finally {
      setLoading(false);
    }
  };

  const getDefaultVoice = (agent: Agent): VoiceOption => {
    return (
      agent.optional_voices.find(
        (voice) => voice.id === agent.default_voiceId
      ) || agent.optional_voices[0]
    );
  };

  const getSelectedVoice = (): VoiceOption => {
    if (agents.length > 0 && selectedUseCase < agents.length) {
      const currentAgent = agents[selectedUseCase];
      return (
        currentAgent.optional_voices[selectedVoiceIndex] ||
        getDefaultVoice(currentAgent)
      );
    }
    return { id: "", name: "", icon: "" };
  };

  useEffect(() => {
    if (agents.length > 0 && selectedUseCase < agents.length) {
      const currentAgent = agents[selectedUseCase];
      const currentVoice = getSelectedVoice();

      setSelectedConfig({
        agent: currentAgent,
        voiceProvider: currentAgent.voiceProvider,
        selectedVoice: currentVoice
      });
    }
  }, [selectedUseCase, selectedVoiceIndex, agents]);

  useEffect(() => {
    setSelectedVoiceIndex(0);
  }, [selectedUseCase]);

  const toggleDialog = () => {
    setIsDialogOpen(!isDialogOpen);
  };

  const handleEndCall = () => {
    setIsCallActive(false);
    setIsDialogOpen(false);
  };

  const handleUseCaseSelect = (index: number) => {
    setSelectedUseCase(index);
  };

  const handleVoiceSelect = (index: number) => {
    setSelectedVoiceIndex(index);
  };

  const toggleChatDialog = () => {
    setIsChatDialogOpen(!isChatDialogOpen);
  };

  const handleCloseChat = () => {
    setIsChatDialogOpen(false);
  };

  const renderVoiceDashboard = () => {
    if (!selectedConfig) return null;

    const dashboardProps = {
      pageCategory: page_category,
      isDialogOpen,
      toggleDialog,
      agentName: selectedConfig.selectedVoice.name,
      callHints: selectedConfig.agent.callHints,
      callHintsTable: selectedConfig.agent.callHintsTable,
      callTitle: selectedConfig.agent.callTitle || "",
      icon: selectedConfig.selectedVoice.icon,
      phoneNumber: selectedConfig.agent.phoneNumber,
      features: selectedConfig.agent.featureList,
      try_speaking: selectedConfig.agent.try_speaking || [],
      activities: selectedConfig.agent.activities || []
    };

    switch (selectedConfig.voiceProvider) {
      case "retell":
        return (
          <RetellCallDashboard
            {...dashboardProps}
            onEndCall={handleEndCall}
            agentId={selectedConfig.agent.id}
            voiceId={selectedConfig.selectedVoice.id ?? ""}
            defaultVoiceId={selectedConfig.agent.default_voiceId}
          />
        );
      case "voicex":
        return (
          <VoicexCallDashboard
            {...dashboardProps}
            voiceConfig={{
              ...selectedConfig.agent.voiceConfig,
              selectedVoiceId: selectedConfig.selectedVoice.id
            }}
            dynamicVariables={{}}
            ttsConfigId={
              selectedConfig.selectedVoice.id === "default"
                ? null
                : selectedConfig.selectedVoice.id ?? ""
            }
          />
        );
      case "outbound-call":
        return (
          <OutboundCallDashboard
            {...dashboardProps}
            voiceConfig={{
              ...selectedConfig.agent.voiceConfig,
              selectedVoiceId: selectedConfig.selectedVoice.id
            }}
            dynamicVariables={{}}
            ttsConfigId={
              selectedConfig.selectedVoice.id === "default"
                ? null
                : selectedConfig.selectedVoice.id ?? ""
            }
          />
        );

      default:
        return (
          <div className="flex items-center justify-center h-full w-full bg-gray-100">
            <div className="text-center">
              <h2 className="font-inter text-2xl font-semibold mb-4">
                Unsupported Voice Provider
              </h2>
              <p className="font-manrope text-gray-600 mb-4">
                Voice provider "{selectedConfig.voiceProvider}" is not
                supported.
              </p>
              <button
                onClick={toggleDialog}
                className="font-manrope mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Close
              </button>
            </div>
          </div>
        );
    }
  };

  const renderChatDashboard = () => {
    if (!selectedConfig) return null;

    return (
      <div className="flex items-center justify-center h-full w-full bg-black">
        <ChatWindow
          onClose={handleCloseChat}
          selectedchatAgent={selectedConfig.agent.id}
          name={selectedConfig.agent.name}
          chatHints={selectedConfig.agent.chatHints || []}
          chatIcon={selectedConfig.selectedVoice.icon}
          agentRole={selectedConfig.agent.callTitle}
          agentName={selectedConfig.agent.agentName}
          features={selectedConfig.agent.featureList || []}
          activities={selectedConfig.agent.activities || []}
          description={selectedConfig.agent.description}
          language={selectedConfig.agent.language}
          agentId={selectedConfig.agent.id}
        />
      </div>
    );
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <span className="font-manrope">Loading...</span>
      </div>
    );
  }

  // Check if current agent has chat enabled to adjust panel height
  const hasChat = selectedConfig?.agent.chat === "true";
  const panelHeight = hasChat ? "calc(100vh - 64px)" : "calc(100vh - 64px)";

  return (
    <div
      className={`flex items-center justify-center`}
      style={{ minHeight: "calc(100vh - 64px)" }}
    >
      <div
        className="w-full max-w-none md:w-[1000px] bg-gray-50"
        style={{ minHeight: "calc(100vh - 64px)" }}
      >
        <div className="min-h-full bg-white">
          <div className="flex flex-col md:flex-row h-full md:space-x-8 md:justify-center md:items-center">
            {/* Left Panel - Form Section */}
            <div className="w-full md:flex-1 p-4 md:p-12 bg-white flex flex-col h-full overflow-y-auto">
              <div className="mt-6 mb-6 md:mb-12 text-left">
                <h1 className="font-inter text-3xl md:text-5xl font-medium text-gray-800 mb-2">
                  {page_title.split(" ").slice(0, 1).join(" ")}
                </h1>
                <h1 className="font-inter text-3xl md:text-5xl font-medium text-blue-600">
                  {page_title.split(" ").slice(1).join(" ")}
                </h1>
              </div>

              <div className="flex items-center mb-6 md:mb-8 justify-start gap-2">
                <div className="bg-blue-50 rounded-xl p-3">
                  <HeartPulse size={24} className="text-blue-600" />
                </div>
                <span className="font-inter text-2xl md:ml-2 md:text-3xl  font-medium text-gray-800 ">
                  {page_category}
                </span>
              </div>

              <div className="w-full h-px bg-gray-200 mb-6 md:mb-8"></div>

              <div className="mb-8 md:mb-12 text-left flex-shrink-0">
                <h2 className="font-inter text-base md:text-lg  text-gray-500 mb-3 md:mb-4">
                  Select Usecase
                </h2>
                <div className="relative">
                  <div className="space-y-3 max-h-[320px] overflow-y-auto">
                    {agents &&
                      agents.map((item, index) => (
                        <UseCaseCard
                          key={index}
                          title={item.name}
                          description={item.description}
                          isSelected={selectedUseCase === index}
                          isFirst={index === 0}
                          onClick={() => handleUseCaseSelect(index)}
                        />
                      ))}
                  </div>
                  {agents && agents.length > 4 && (
                    <div className="text-center mt-12 flex flex-row justify-center gap-1">
                      <MouseIcon
                        size={14}
                        className="text-gray-400 mt-0.5"
                        strokeWidth={1.8}
                      />
                      <div className="text text-gray-400">
                        Scroll to see more
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-left flex-shrink-0">
                <h2 className="font-inter text-base md:text-lg font-medium text-gray-500 mb-4">
                  Select Voice
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
                  {agents.length > 0 &&
                    selectedUseCase < agents.length &&
                    agents[selectedUseCase].optional_voices.map(
                      (voice, index) => (
                        <AgentCard
                          key={voice.id}
                          name={voice.name}
                          language={voice.language}
                          region={voice.region}
                          image={voice.icon || ""}
                          isSelected={selectedVoiceIndex === index}
                          onClick={() => handleVoiceSelect(index)}
                          description={voice.description}
                        />
                      )
                    )}
                </div>
              </div>
            </div>

            {/* Right Panel - Agent Display Section */}
            <div
              className="w-full md:flex-1 bg-white flex sm:items-center justify-center p-4 md:p-8 min-h-[600px] sm:min-w-[450px]"
              style={{ height: panelHeight }}
            >
              <div
                className="w-full max-w-sm md:max-w-md md:w-4/5 h-[600px] min-h-[500px] rounded-[2rem] md:rounded-[2.5rem] flex items-center justify-center overflow-hidden"
                style={{
                  background:
                    "linear-gradient( 220deg,#0d1b3d 0%,rgb(17, 23, 100) 25%,rgb(34, 48, 138) 50%,rgb(45, 118, 178) 75%,rgb(109, 206, 251) 100%)",
                  height: hasChat ? "700px" : "600px"
                }}
              >
                <div className="flex-1 flex flex-col items-center justify-between relative z-10 px-4 py-6 h-full">
                  <div className="flex-1 flex flex-col items-center justify-center">
                    <div className="relative mb-4 md:mb-8">
                      <div className="w-32 h-32 md:w-[200px] md:h-[200px] lg:w-[250px] lg:h-[250px] relative">
                        <div className="absolute inset-8 mt-1 mx-0.5 md:inset-[60px] rounded-full overflow-hidden bg-gray-300 z-10">
                          <img
                            src={selectedConfig?.selectedVoice.icon || ""}
                            alt={selectedConfig?.selectedVoice.name || "Agent"}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-center text-center">
                      <h1 className="font-inter text-lg md:text-2xl lg:text-3xl font-normal text-white">
                        {selectedConfig?.agent.agentName || "Agent"}
                      </h1>

                      <h2 className="font-inter text-sm md:text-lg lg:text-xl text-white/80 mt-1 md:mt-2">
                        speaks {selectedConfig?.agent.language || "English"}
                      </h2>

                      <span
                        className="font-manrope text-xs md:text-sm lg:text-base text-white/60 max-w-xs md:max-w-sm lg:max-w-md px-2 md:px-4 lg:px-20 mt-2 md:mt-4 leading-relaxed"
                        style={{ whiteSpace: "pre-line" }}
                      >
                        {selectedConfig?.agent.subDescription?.replace(
                          /,/g,
                          ",\n"
                        ) || ""}
                      </span>
                    </div>
                  </div>

                  {/* Buttons Section - Vertically Aligned */}
                  <div className="mt-4 md:mt-8 flex flex-col items-center gap-4 w-full">
                    {/* Talk to Agent Button - Only show if voice is enabled */}
                    {selectedConfig?.agent.voice === "true" && (
                      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                          <button
                            className="font-manrope flex items-center text-black justify-center gap-2 px-6 md:px-8 lg:px-10 py-3 md:py-4 rounded-[40px] border border-[#EBEAEA] transition-all duration-300 hover:scale-105 hover:shadow-lg text-sm md:text-base font-medium w-full max-w-[280px]"
                            style={{
                              background: "#FFFFFF"
                            }}
                          >
                            <Phone
                              size={16}
                              className="md:w-5 md:h-5 lg:w-6 lg:h-6"
                            />
                            <span>Talk to Agent</span>
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-none h-screen w-screen p-0 border-none [&>button]:hidden">
                          {renderVoiceDashboard()}
                        </DialogContent>
                      </Dialog>
                    )}

                    {/* Start New Chat Button - Only show if chat is enabled */}
                    {selectedConfig?.agent.chat === "true" && (
                      <Dialog open={isChatDialogOpen} onOpenChange={setIsChatDialogOpen}>
                        <DialogTrigger asChild>
                          <button
                            className="font-manrope flex items-center text-white justify-center gap-2 px-6 md:px-8 lg:px-10 py-3 md:py-4 rounded-[40px] border border-white/20 transition-all duration-300 hover:scale-105 hover:shadow-lg text-sm md:text-base font-medium w-full max-w-[280px]"
                            style={{
                              background: "rgba(255, 255, 255, 0.15)",
                              backdropFilter: "blur(10px)"
                            }}
                          >
                            <MessageSquare
                              size={16}
                              className="md:w-5 md:h-5 lg:w-6 lg:h-6"
                            />
                            <span>Start New Chat</span>
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-none h-screen w-screen p-0 border-none [&>button]:hidden">
                          {renderChatDashboard()}
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
